import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AdvancedSearch from "../../components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import { useMutationData } from "../../hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { AdjustIcon } from "../Icons";
import TableWithPagination from "../Tables/TableWithPagination";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useEscapeKey from "@hooks/useEscapeKey";

const INITIAL_USER_SEARCH_CRITERIA = {
  userId: "",
  userName: "",
  deptCdName: "",
  userIdForSearch: "",
};

const MemberSelectionModal = ({ projectManagerId = null, isOpen, onClose, creatorList, departmentList, onSelect, selectedMember = null, onRemoveMember = null, "data-testid": dataTestId = "member-selection-modal" }) => {
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [userList, setUserList] = useState([]);
  const [totalUserData, setTotalUserData] = useState(0);
  const [totalUserPages, setTotalUserPages] = useState(0);
  const [searchUserCriteria, setSearchUserCriteria] = useState(INITIAL_USER_SEARCH_CRITERIA);
  const [debouncedSearchCriteria, setDebouncedSearchCriteria] = useState(searchUserCriteria);
  const [currentUserPage, setCurrentUserPage] = useState(1);
  const [selectedUserItem, setSelectedUserItem] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimerRef = useRef(null);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.MEMBER_SELECT_LIST, DEFAULT_ITEMS_PER_PAGE);
  const isInitialMount = useRef(true);

  const tempSelectedUserID = useMemo(() => {
    if (!Array.isArray(selectedUserItem)) return [];
    return selectedUserItem.map(item => item?.userId).filter(Boolean);
  }, [selectedUserItem]);

  // Track members that were initially selected (existing members)
  const [initialSelectedMembers, setInitialSelectedMembers] = useState([]);
  // Track members that were unchecked (to be removed)
  const [uncheckedMembers, setUncheckedMembers] = useState([]);

  // Constants for Advanced Search
  const advancedSearchFields = [
    {
      name: "createrId",
      label: t("common.creator"),
      type: "select",
      keepRawLabel: true,
      options: creatorList,
      displayFormatLabelWithID: true,
      placeholder: t("common.please-select-creator"),
    },
    // {
    //   name: "deptCd",
    //   label: t("common.department"),
    //   type: "select",
    //   keepRawLabel: true,
    //   options: departmentList.map((item) => ({
    //     label: translateDepartment(t, item.value),
    //     value: item.value,
    //   })),
    //   placeholder: t("common.please-select-department"),
    // },
  ];

  const { showAdvancedSearch, setShowAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: advancedSearchFields,
    quickSearchField: "userIdForSearch",
    setSearchCriteria: setSearchUserCriteria,
    initialCriteria: INITIAL_USER_SEARCH_CRITERIA,
  });

  // Call API to get user data
  const getUserListData = useMutationData(
    "user_for_project_member",
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setUserList(response.object);
          setTotalUserData(response.objectCount);
          setTotalUserPages(Math.ceil(response.objectCount / itemsPerPage));
        }
      },
      onError: (error) => {
        console.error("Error during get data:", error);
      },
    },
  );

  const fetchUserData = (data) => {
    const effectiveData = resolveSearchCriteria(data, {
      quickSearchField: "userIdForSearch",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveData).reduce((acc, key) => {
      const value = effectiveData[key];
      if (value !== "" && value !== undefined && value !== null) {
        if ((key === "createdAtFrom" || key === "createdAtTo") && value instanceof Date) {
          acc[key] = value.toISOString().split("T")[0]; // Format: YYYY-MM-DD
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});

    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentUserPage - 1);
    bodyRequest["enableFlag"] = "Y";
    bodyRequest["projectManagerId"] = projectManagerId;
    getUserListData.mutate(bodyRequest);
  };

  // -----------------------------------------------------------------------------
  // EFFECT 1: Handle open and close modal
  // -----------------------------------------------------------------------------
  useEffect(() => {
    // When opening modal
    if (isOpen) {
      // Set flag to indicate that the modal has been mounted, so we can skip the child effects
      isInitialMount.current = true;

      setShowAdvancedSearch(false);

      // Reset selection state and initialize from selectedMember prop
      if (selectedMember && Array.isArray(selectedMember)) {
        setSelectedUserItem(selectedMember);
        setInitialSelectedMembers(selectedMember);
        setUncheckedMembers([]);
      } else if (selectedMember) {
        setSelectedUserItem([selectedMember]);
        setInitialSelectedMembers([selectedMember]);
        setUncheckedMembers([]);
      } else {
        setSelectedUserItem([]);
        setInitialSelectedMembers([]);
        setUncheckedMembers([]);
      }

      // Call API initially - only once
      const bodyRequest = {
        pageSize: itemsPerPage,
        startRow: 0,
        enableFlag: "Y",
        projectManagerId: projectManagerId,
      };
      getUserListData.mutate(bodyRequest);

      // Initial fetch above replaces EFFECT 3's first run; unblock debounced search afterward
      queueMicrotask(() => {
        isInitialMount.current = false;
      });
    } else {
      // When closing modal (cleanup)
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      setCurrentUserPage(1);
    }
  }, [isOpen, selectedMember]);


  // -----------------------------------------------------------------------------
  // EFFECT 2: Handle debounce when user types search
  // -----------------------------------------------------------------------------
  useEffect(() => {
    // Skip if modal is not open
    if (!isOpen) return;

    // Skip first run when search criteria is reset when opening modal
    if (isInitialMount.current) return;

    setIsSearching(true);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      // When user types search, always reset to page 1
      if (currentUserPage !== 1) {
        setCurrentUserPage(1);
      }
      setDebouncedSearchCriteria(searchUserCriteria);
      setIsSearching(false);
    }, 800);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchUserCriteria]);


  // -----------------------------------------------------------------------------
  // EFFECT 3: Handle call API when user types search
  // -----------------------------------------------------------------------------
  useEffect(() => {
    // Skip if modal is not open
    if (!isOpen) return;

    // Skip first run when component mounts
    // isInitialMount.current will be set to false after the first run
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Call
    fetchUserData(debouncedSearchCriteria);

  }, [debouncedSearchCriteria, currentUserPage, itemsPerPage, showAdvancedSearch]);

  // Remove unused states that might conflict with TableWithPagination

  // const userTableColumns = ["userId", "userName", "deptCdName", "email"];
  const userTableColumns = ["userId", "userName", "email"];
  const userTableColumnConfig = {
    userId: {
      style: {
        width: "25%",
        whiteSpace: "normal",
        padding: "0.5rem",
        verticalAlign: "middle",
      },
      headerRender: () => t("common.user-id"),
      customRender: (value, row) => row.userId,
    },
    userName: {
      style: {
        width: "30%",
        whiteSpace: "normal",
        overflowWrap: "initial",
        padding: "0.5rem",
        verticalAlign: "middle",
      },
      headerRender: () => t("common.user-name"),
      customRender: (value, row) => row.userName,
    },
    // deptCdName: {
    //   style: {
    //     width: "25%",
    //     whiteSpace: "normal",
    //     overflowWrap: "initial",
    //     padding: "0.5rem",
    //     verticalAlign: "middle",
    //   },
    //   headerRender: () => t("common.department-name"),
    //   customRender: (value, row) => translateDepartment(t, row.deptCdName),
    // },
    email: {
      style: {
        width: "35%",
        whiteSpace: "normal",
        overflowWrap: "initial",
        padding: "0.5rem",
        verticalAlign: "middle",
      },
      headerRender: () => t("common.email"),
      customRender: (value, row) => row.email,
    },
  };

  const handleCancel = () => {
    // Reset selection when canceling
    setSelectedUserItem([]);
    setUncheckedMembers([]);
    setInitialSelectedMembers([]);
    setSearchUserCriteria({ ...INITIAL_USER_SEARCH_CRITERIA });
    setDebouncedSearchCriteria({ ...INITIAL_USER_SEARCH_CRITERIA });
    setIsSearching(false);
    // Clear timeout to avoid memory leak
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    onClose();
  };


  // Handler for Advanced Search
  const handleAdvancedSearch = (searchData) => {
    setSearchUserCriteria((prev) => ({
      ...prev,
      ...searchData,
    }));
  };

  const handleSelect = () => {
    // Check if there are unchecked members that need to be removed
    if (uncheckedMembers.length > 0 && onRemoveMember) {
      // Call onRemoveMember with unchecked members
      onRemoveMember(uncheckedMembers);
    }

    // Filter out unchecked members from selectedUserItem before calling onSelect
    const finalSelectedMembers = selectedUserItem.filter(
      (member) => !uncheckedMembers.some((unchecked) => unchecked.userId === member.userId),
    );

    // Call onSelect with final selected array items
    onSelect(finalSelectedMembers);

    // Reset all state when closing
    setSelectedUserItem([]);
    setUncheckedMembers([]);
    setInitialSelectedMembers([]);
    setSearchUserCriteria({ ...INITIAL_USER_SEARCH_CRITERIA });
    setDebouncedSearchCriteria({ ...INITIAL_USER_SEARCH_CRITERIA });
    setIsSearching(false);

    // Clear timeout to avoid memory leak
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    onClose();
  };

  const handleOnRowMemberListClick = (data) => {
    const userId = data.userId;
    const isCurrentlySelected = tempSelectedUserID.includes(userId);

    if (isCurrentlySelected) {
      // If already selected, unselect it
      const newSelectedIds = tempSelectedUserID.filter((id) => id !== userId);
      // Update selection state using helper function
      updateSelectionState(newSelectedIds);
    } else {
      // If not selected, add it
      const newSelectedIds = [...tempSelectedUserID, userId];
      // Update selection state using helper function
      updateSelectionState(newSelectedIds);
    }
  };

  // Helper function to update selection state consistently
  const updateSelectionState = (newSelectedIds) => {
    // Get items from current page that are selected/deselected
    const currentPageItems = userList.filter((item) => newSelectedIds.includes(item.userId));

    // Get items selected from other pages (not in current userList)
    const itemsFromOtherPages = selectedUserItem.filter(
      (item) => !userList.some((currentItem) => currentItem.userId === item.userId),
    );

    // Combine items from current page and items from other pages
    let allSelectedItems = [...itemsFromOtherPages, ...currentPageItems];

    // Update selectedUserItem with the combined selection
    setSelectedUserItem(allSelectedItems);

    // Update unchecked members based on current selection
    const newUncheckedMembers = initialSelectedMembers.filter(
      (member) => !allSelectedItems.some((selectedItem) => selectedItem.userId === member.userId),
    );
    setUncheckedMembers(newUncheckedMembers);
  };

  // Handle checkbox change - now using same logic as row click for consistency
  const handleCheckboxChange = (selectedRows) => {
    // Update selection state using helper function
    updateSelectionState(selectedRows);
  };

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentUserPage(1); // Reset to page 1 when changing number of rows
  };

  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start md:justify-center backdrop-blur-[1px] overflow-x-auto p-4`}
      data-testid={`${dataTestId}-overlay`}
    >
      <div className={`${colors.bgModal} max-h-[80vh] w-[800px] min-w-[800px] flex-shrink-0 rounded-lg shadow-xl flex flex-col`} data-testid={dataTestId}>
        {/* Header */}
        <div className={`px-6 py-4 flex-shrink-0`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold`} data-testid={`${dataTestId}-title`}>{t("common.select-user")}</h2>
          </div>
        </div>

        {/* Search Section */}
        <div className="space-y-4 px-6 pb-4 flex-shrink-0">
          {/* Filter Inputs */}
          <div className="grid grid-cols-3 gap-4">
            <div className="relative md:col-span-2">
              <MagnifyingGlassIcon
                className={`absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform ${colors.textSecondary}`}
              />
              <input
                type="text"
                placeholder={t("common.search-for-user-id")}
                value={searchUserCriteria.userIdForSearch}
                onChange={(e) => setSearchUserCriteria((prev) => ({ ...prev, userIdForSearch: e.target.value }))}
                className={`w-full rounded-lg border px-3 py-2 pr-8 pl-10 text-sm focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                data-testid={fieldInputTestId("search")}
              />
              {isSearching && (
                <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                  <div className={`h-3 w-3 animate-spin rounded-full border-b-2 ${colors.borderLoader}`}></div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleAdvancedSearchToggle}
              className={`inline-flex items-center justify-center border px-4 py-2 ${colors.borderOutLineButton} rounded-md text-sm font-medium shadow-sm ${colors.textActive} ${colors.hoverOutlineModalSelection} whitespace-nowrap`}
              data-testid={fieldInputTestId("advanced-search-toggle")}
            >
              <AdjustIcon className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">{t("common.advanced-search")}</span>
              <span className="sm:hidden">{t("common.advanced")}</span>
            </button>
          </div>

          {/* Search Actions */}
          {/* <div className="flex justify-center gap-3 mt-4">
                        <button
                            onClick={handleReset}
                            className={`px-4 py-2 text-sm border rounded-lg ${colors.borderPrimary} ${colors.textSecondary} hover:${colors.bgSecondary} transition-colors`}
                        >
                            Reset
                        </button>
                    </div> */}
        </div>

        {/* Advanced Search */}
        {showAdvancedSearch && (
          <div className="relative mx-6 flex-shrink-0">
            <div
              className={`absolute -top-2 z-10 h-0 w-0`}
              style={{
                right: "calc(33.333% / 2 - 16px)",
                borderLeft: "8px solid transparent",
                borderRight: "8px solid transparent",
                borderBottom: `8px solid #374151`,
                filter: `drop-shadow(0 0 0 1px #292D33)`,
              }}
            ></div>

            <AdvancedSearch
              title={t("common.advanced-search")}
              searchFields={advancedSearchFields}
              searchHandler={handleAdvancedSearch}
              withoutSearch={true}
              gridCols="md:grid-cols-2 lg:grid-cols-3"
            />
          </div>
        )}

        {/* Table Section - Scrollable */}
        <div className="flex flex-1 flex-col px-6 min-h-0">
          <TableWithPagination
            columns={userTableColumns}
            columnConfig={userTableColumnConfig}
            data={userList}
            currentPage={currentUserPage}
            setCurrentPage={setCurrentUserPage}
            loading={getUserListData.isPending || isSearching}
            rowKey="userId"
            totalPages={totalUserPages}
            totalData={totalUserData}
            maxRowsPerPage={itemsPerPage}
            hasCheckbox={true}
            selectedRows={tempSelectedUserID}
            onCheckboxChange={handleCheckboxChange}
            onRowClick={handleOnRowMemberListClick}
            onItemsPerPageChange={handleItemsPerPageChangeWithReset}
            showPagination={true}
            scrollableBody={true}
            showItemsPerPageSelector={true}
          />
        </div>

        {/* Footer - Sticky */}
        <div className={`px-6 py-4 ${colors.borderPrimary} flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
          <div className="flex items-center justify-end">
            <div className="flex items-center space-x-3">
              <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={handleCancel}>{t("common.cancel-button")}</OutlineButton>
              <PrimaryButton data-testid={`${dataTestId}-confirm-button`} onClick={handleSelect} disabled={!selectedUserItem.length}>
                {t("common.select")}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberSelectionModal;
